import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useSignupStore } from "@/store/useSignupStore";
import { useInstanceStateStore } from "@/store/useInstanceStateStore";
import { usePersonaStore } from "@/store/usePersonaStore";
import { useBillingStore } from "@/store/useBillingStore";
import { Sparkles, ArrowRight, ArrowLeft, CheckCircle2, Loader2, Mail, MessageCircle, Lock, Clock, Play, BookOpen, ListChecks, CreditCard } from 'lucide-react';
import { Logo } from "@/components/common/Logo";

// =====================================================================
// Phase 11a — Funnel: Signup → Provisioning → Instance Ready → Welcome
// V2 unlock: single signup flow. No intent-branching (hardware/cloud removed).
// Single consolidated consent checkbox (Privacy + ToS).
// =====================================================================

function FunnelNav() {
  return (
    <nav className="sticky top-0 z-20 bg-white/85 backdrop-blur border-b border-slate-200/70">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-3 min-w-0">
        <Link to="/" className="shrink-0">
          <Logo size={22} />
        </Link>
        <div className="ml-auto inline-flex items-center gap-1 text-[10.5px] sm:text-[11.5px] text-slate-500 truncate min-w-0">
          <Lock size={11} className="shrink-0" />
          <span className="truncate">Secure signup · SOC 2 · GDPR</span>
        </div>
      </div>
    </nav>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{label}</label>
      {children}
    </div>
  );
}
Field.propTypes = {
  children: PropTypes.node,
  label: PropTypes.string,
};

// ---------------------------------------------------------------------
// /signup — lead capture
// ---------------------------------------------------------------------
export function SignupPage() {
  const navigate = useNavigate();
  const { data, setData, createReservation } = useSignupStore();

  const [agree, setAgree] = useState(!!(data.agreePrivacy && data.agreeTOS));
  const [trialChoice, setTrialChoice] = useState("no-card"); // 'no-card' | 'with-card'
  const [card, setCard] = useState({ number: "", expiry: "", cvc: "", zip: "" });
  const initTrial = useBillingStore((s) => s.initTrial);

  const cardValid = trialChoice === "no-card" || (card.number.trim().length >= 4 && card.expiry.trim() && card.cvc.trim());
  const valid = data.founderName.trim().length > 1 && /\S+@\S+\.\S+/.test(data.email) && data.company.trim().length > 1 && agree && cardValid;

  const submit = () => {
    if (!agree) { toast.error("Please agree to the Privacy Policy and Terms of Service."); return; }
    if (!valid) { toast.error("Please complete every field."); return; }
    // Persist combined consent (both flags) + tos version (agree_tos_version: 2 per V2)
    const payload = { ...data, agreeTOS: true, agreePrivacy: true, agreeTosVersion: 2 };
    initTrial({ withCard: trialChoice === "with-card" });
    const rid = createReservation(payload);
    navigate(`/provisioning/${rid}`);
  };

  return (
    <div className="min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-white">
      <FunnelNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> Start your trial</div>
        <h1 className="mt-2 text-[26px] sm:text-[34px] md:text-[40px] font-semibold tracking-tight text-slate-900 leading-tight">A few details and Maya is yours.</h1>
        <p className="mt-2 text-[14px] text-slate-600 max-w-lg">No credit card. 14-day full trial. We&apos;ll provision your private instance in about 5 minutes.</p>

        <div className="mt-8 space-y-5 min-w-0">
          <Field label="Your name" id="su-name">
            <Input value={data.founderName} onChange={(e) => setData({ founderName: e.target.value })} placeholder="e.g. Sarah Chen" className="h-11 sm:h-10 text-[13.5px] w-full min-w-0" data-testid="signup-name" autoComplete="name" />
          </Field>
          <Field label="Work email" id="su-email">
            <Input value={data.email} onChange={(e) => setData({ email: e.target.value })} placeholder="sarah@stripe.com" type="email" inputMode="email" className="h-11 sm:h-10 text-[13.5px] w-full min-w-0" data-testid="signup-email" autoComplete="email" />
          </Field>
          <Field label="Company" id="su-company">
            <Input value={data.company} onChange={(e) => setData({ company: e.target.value })} placeholder="Stripe" className="h-11 sm:h-10 text-[13.5px] w-full min-w-0" data-testid="signup-company" autoComplete="organization" />
          </Field>
          <Field label="Role">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
              {["Founder/CEO", "Cofounder", "Head of Sales", "Head of Marketing", "Solo", "Other"].map((r) => (
                <button key={r} type="button" onClick={() => setData({ role: r })} className={cn("min-h-10 text-[11.5px] font-medium rounded-md px-2.5 py-2 ring-1 transition-colors", data.role === r ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50")} data-testid={`signup-role-${r}`}>{r}</button>
              ))}
            </div>
          </Field>
          <Field label="Company size">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {["1", "2-10", "11-50", "50+"].map((s) => (
                <button key={s} type="button" onClick={() => setData({ companySize: s })} className={cn("min-h-10 text-[11.5px] font-medium rounded-md px-2.5 py-2 ring-1 transition-colors", data.companySize === s ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50")} data-testid={`signup-size-${s}`}>{s}</button>
              ))}
            </div>
          </Field>
          <Field label="Sales motion">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
              {["Inbound-led", "Outbound-led", "Mixed"].map((m) => (
                <button key={m} type="button" onClick={() => setData({ salesMotion: m })} className={cn("min-h-10 text-[11.5px] font-medium rounded-md px-2.5 py-2 ring-1 transition-colors", data.salesMotion === m ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50")}>{m}</button>
              ))}
            </div>
          </Field>

          {/* Trial choice — how to start */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4" data-testid="trial-choice-section">
            <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> How would you like to start?</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
              <button type="button" onClick={() => setTrialChoice("no-card")} className={cn("relative text-left rounded-xl border bg-white p-3 transition-all", trialChoice === "no-card" ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200 hover:border-slate-300")} data-testid="trial-choice-no-card">
                <span className="absolute -top-2 right-3 inline-flex items-center gap-1 rounded-full bg-indigo-600 text-white px-2 py-0.5 text-[9.5px] uppercase tracking-wider font-semibold">Recommended</span>
                <div className="text-[13px] font-semibold text-slate-900">Start 14-day trial</div>
                <div className="text-[11.5px] text-indigo-700 font-semibold">No card required</div>
                <div className="mt-1 text-[11px] text-slate-500 leading-snug">Try Maya for 14 days. Upgrade anytime — your data stays.</div>
              </button>
              <button type="button" onClick={() => setTrialChoice("with-card")} className={cn("relative text-left rounded-xl border bg-white p-3 transition-all", trialChoice === "with-card" ? "border-indigo-500 ring-2 ring-indigo-200" : "border-slate-200 hover:border-slate-300")} data-testid="trial-choice-with-card">
                <div className="text-[13px] font-semibold text-slate-900">Start 14-day trial</div>
                <div className="text-[11.5px] text-indigo-700 font-semibold">Add card now</div>
                <div className="mt-1 text-[11px] text-slate-500 leading-snug">You won&apos;t be charged until day 15. Cancel anytime before then.</div>
              </button>
            </div>
            {trialChoice === "with-card" && (
              <div className="mt-3 rounded-xl bg-slate-50/60 ring-1 ring-slate-200 p-3 space-y-2" data-testid="trial-card-form">
                <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1"><CreditCard size={11} /> Payment details</div>
                <Input placeholder="Card number" value={card.number} onChange={(e) => setCard({ ...card, number: e.target.value })} className="h-11 sm:h-9 text-[12.5px] w-full min-w-0" inputMode="numeric" data-testid="signup-card-number" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <Input placeholder="MM/YY" value={card.expiry} onChange={(e) => setCard({ ...card, expiry: e.target.value })} className="h-11 sm:h-9 text-[12.5px] w-full min-w-0" data-testid="signup-card-expiry" />
                  <Input placeholder="CVC" value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className="h-11 sm:h-9 text-[12.5px] w-full min-w-0" inputMode="numeric" data-testid="signup-card-cvc" />
                  <Input placeholder="Zip" value={card.zip} onChange={(e) => setCard({ ...card, zip: e.target.value })} className="h-11 sm:h-9 text-[12.5px] w-full min-w-0" data-testid="signup-card-zip" />
                </div>
                <div className="text-[10.5px] text-slate-500 inline-flex items-start gap-1"><Lock size={10} className="mt-0.5 shrink-0" /> Encrypted with 256-bit TLS · Never charged during the 14-day trial</div>
              </div>
            )}
          </div>

          {/* Single consolidated consent — Privacy + ToS */}
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
            <label className="flex items-start gap-2 text-[12px] text-slate-800 cursor-pointer">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 h-4 w-4 shrink-0" data-testid="signup-agree" />
              <span>I agree to the Sellyx <a href="/privacy" className="text-indigo-700 underline">Privacy Policy</a> and <a href="/terms" className="text-indigo-700 underline">Terms of Service</a>.</span>
            </label>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
            <Link to="/" className="inline-flex min-h-10 items-center justify-center sm:justify-start gap-1 text-[12px] text-slate-500 hover:text-slate-700"><ArrowLeft size={12} /> Back</Link>
            <Button size="lg" className="h-11 text-[13px] px-5 w-full sm:w-auto" disabled={!valid} onClick={submit} data-testid="signup-submit">
              Start your free trial <ArrowRight size={13} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function generateSlots() {
  // V2 unlock: hardware sales-confirm slot generation no longer used. Kept as no-op stub
  // to avoid accidental imports breaking; can be deleted in a future cleanup pass.
  return [];
}

function getStageState(currentStage, stageId) {
  if (currentStage > stageId) return "done";
  if (currentStage === stageId) return "active";
  return "pending";
}

function getStageCardClassName(state) {
  if (state === "active") return "border-indigo-300 ring-2 ring-indigo-100";
  if (state === "done") return "border-emerald-200 bg-emerald-50/30";
  return "border-slate-200";
}

function getStageIconClassName(state) {
  if (state === "done") return "bg-emerald-500 text-white";
  if (state === "active") return "bg-indigo-600 text-white";
  return "bg-slate-100 text-slate-400";
}

function renderStageIcon(state, stageId) {
  if (state === "done") return <CheckCircle2 size={14} />;
  if (state === "active") return <Loader2 size={13} className="animate-spin" />;
  return <span className="text-[11px] font-semibold">{stageId}</span>;
}

// ---------------------------------------------------------------------
// /provisioning/:reservationId
// ---------------------------------------------------------------------
const STAGES = [
  { id: 1, title: "Account created",                          detail: "Welcome aboard.",                                             duration: 5 },
  { id: 2, title: "Provisioning your private container",      detail: "Allocating compute in us-west-2 · Single-tenant K8s pod.",     duration: 85 },
  { id: 3, title: "OpenClaw runtime initializing",            detail: "Loading agent runtime v2.4.1 · Tool allowlist configured.",    duration: 110 },
  { id: 4, title: "Maya is warming up",                       detail: "Loading GPT-5.5 weights · Pinecone-east-1 vector store.",      duration: 200 },
  { id: 5, title: "Ready",                                    detail: "All systems green.",                                          duration: 0 },
];

const FAST_FORWARD = { 1: 0.5, 2: 1, 3: 1, 4: 1.5, 5: 0 }; // 4-second total demo mode

export function ProvisioningPage() {
  const { reservationId } = useParams();
  const navigate = useNavigate();
  const { data } = useSignupStore();
  const personaName = usePersonaStore((s) => s.name);
  const setPersonaName = usePersonaStore((s) => s.setName);
  const [stage, setStage] = useState(1);
  const [elapsed, setElapsed] = useState(0);
  const [fast, setFast] = useState(false);
  const [tab, setTab] = useState("demo");
  const [tempName, setTempName] = useState(personaName);

  const totalSeconds = STAGES.reduce((s, st) => s + (fast ? FAST_FORWARD[st.id] : st.duration), 0);

  useEffect(() => {
    if (stage > 4) return;
    const cur = STAGES.find((s) => s.id === stage);
    const dur = fast ? FAST_FORWARD[stage] : cur.duration;
    const t = setTimeout(() => setStage((s) => s + 1), dur * 1000);
    return () => clearTimeout(t);
  }, [stage, fast]);

  useEffect(() => {
    if (stage > 4) return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [stage]);

  useEffect(() => {
    if (stage === 5) {
      const t = setTimeout(() => navigate(`/instance-ready/${reservationId || "demo"}`), 3000);
      return () => clearTimeout(t);
    }
  }, [stage, navigate, reservationId]);

  const etaSec = Math.max(0, totalSeconds - elapsed);
  const etaMin = Math.ceil(etaSec / 60);

  const firstName = (data.founderName || "there").split(" ")[0];

  return (
    <div className="min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-gradient-to-br from-indigo-50/30 via-white to-violet-50/20">
      <FunnelNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10 grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 min-w-0">
          <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold inline-flex items-center gap-1"><Sparkles size={11} /> Setting things up</div>
          <h1 className="mt-2 text-[24px] sm:text-[30px] md:text-[36px] font-semibold tracking-tight text-slate-900 leading-tight">Setting up your Sellyx instance</h1>
          <p className="mt-2 text-[14px] text-slate-600 max-w-lg">Hi {firstName}, this usually takes 5-7 minutes. We&rsquo;re spinning up your private container, OpenClaw runtime, and warming up {personaName}.</p>
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-slate-200 px-2.5 py-1 text-[11px] text-slate-600">
            <Clock size={11} className="text-indigo-600" />
            ETA: <span className="font-semibold tabular-nums text-slate-900 ml-0.5">~{stage === 5 ? "0" : etaMin} {etaMin === 1 ? "minute" : "minutes"}</span>
          </div>

          {/* Vertical stage timeline */}
          <ol className="mt-6 relative space-y-3">
            {STAGES.map((s) => {
              const state = getStageState(stage, s.id);
              return (
                <li key={s.id} className={cn(
                  "rounded-xl border bg-white p-3 sm:p-4 flex items-start gap-3 transition-all min-w-0",
                  getStageCardClassName(state),
                )} data-testid={`prov-stage-${s.id}`}>
                  <div className={cn("h-7 w-7 rounded-full flex items-center justify-center shrink-0",
                    getStageIconClassName(state),
                  )}>
                    {renderStageIcon(state, s.id)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={cn("text-[13px] font-semibold", state === "pending" ? "text-slate-400" : "text-slate-900")}>{s.title}</div>
                    {state !== "pending" && s.id !== 5 && (
                      <div className="mt-1 rounded-md bg-slate-900 text-slate-100 px-2.5 py-1.5 font-mono text-[10.5px] leading-relaxed break-words">
                        <span className="text-emerald-400">$</span> {s.detail}
                      </div>
                    )}
                    {state === "done" && <div className="text-[10.5px] text-emerald-700 mt-1">Done</div>}
                    {state === "active" && s.id !== 5 && <div className="text-[10.5px] text-indigo-600 mt-1">In progress…</div>}
                    {state === "active" && s.id === 5 && <div className="text-[12px] text-emerald-700 font-semibold mt-1">All systems green · taking you in 3s</div>}
                  </div>
                </li>
              );
            })}
          </ol>

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-[10.5px] text-slate-500 break-words min-w-0">We&rsquo;ll email you at <span className="font-mono text-slate-700 break-all">{data.email || "you"}</span> when {personaName} is ready · You can close this tab safely.</div>
            <button type="button" onClick={() => setFast(true)} className="text-[11px] text-slate-400 hover:text-slate-700 font-medium min-h-10 sm:min-h-0 self-start sm:self-auto shrink-0" data-testid="prov-skip">Skip wait (demo)</button>
          </div>
        </div>

        {/* Right rail — While you wait */}
        <aside className="md:col-span-2 min-w-0">
          <div className="md:sticky md:top-20 space-y-3">
            <div className="rounded-2xl border border-slate-200 bg-white p-3">
              <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1"><Sparkles size={11} className="text-indigo-500" /> While you wait</div>
              <div className="mt-2 grid grid-cols-2 gap-1">
                {[
                  { id: "demo",    Icon: Play,       label: "Watch demo" },
                  { id: "things",  Icon: ListChecks, label: "Top 5 things" },
                  { id: "pitch",   Icon: BookOpen,   label: "90-sec pitch" },
                  { id: "name",    Icon: Sparkles,   label: "Pick a name" },
                ].map((t) => (
                  <button key={t.id} type="button" onClick={() => setTab(t.id)} className={cn("inline-flex min-h-10 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[10.5px] font-medium ring-1 transition-colors", tab === t.id ? "bg-indigo-50 text-indigo-700 ring-indigo-200" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50")} data-testid={`prov-tab-${t.id}`}>
                    <t.Icon size={10} className="shrink-0" /> {t.label}
                  </button>
                ))}
              </div>
              <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/30 p-3 text-[12px] text-slate-700 leading-relaxed min-h-[180px]">
                {tab === "demo" && (
                  <div>
                    <div className="aspect-video rounded-md bg-slate-900 text-white flex items-center justify-center"><Play size={26} /></div>
                    <Link to="/demo" className="block mt-2 text-indigo-700 font-semibold text-[12px] hover:underline">Open the 2-minute demo →</Link>
                  </div>
                )}
                {tab === "things" && (
                  <ul className="space-y-1.5 list-decimal list-inside text-[11.5px]">
                    <li>Reply to WhatsApp DMs in your voice, 24/7</li>
                    <li>Draft Gmail threads from your inbox</li>
                    <li>Run multi-channel sequences from /outreach</li>
                    <li>Watch the web for buying signals about you</li>
                    <li>Brief you every morning at 7:30am</li>
                  </ul>
                )}
                {tab === "pitch" && (
                  <p className="text-[12px] leading-relaxed">Sellyx is a personal AI SDR for founders. {personaName} runs on your WhatsApp, LinkedIn, and Gmail — as you. Customers can&apos;t tell they&apos;re talking to AI because the writing voice is yours, the channels are yours, the number is yours. Five-minute setup. Day-1 ROI.</p>
                )}
                {tab === "name" && (
                  <div className="space-y-2">
                    <div className="text-[11.5px]">Pick a name for your AI SDR. Most founders keep <span className="font-semibold">Maya</span>. Change it any time later.</div>
                    <Input maxLength={30} value={tempName} onChange={(e) => setTempName(e.target.value)} onBlur={() => setPersonaName(tempName)} onKeyDown={(e) => e.key === "Enter" && setPersonaName(tempName)} placeholder="Maya" className="h-11 sm:h-9 text-[13px] font-semibold w-full min-w-0" data-testid="prov-name-input" />
                    <div className="rounded-md bg-white ring-1 ring-slate-200 px-2.5 py-2 text-[11.5px] italic text-slate-700">
                      &ldquo;Hello, I&apos;m <span className="not-italic font-semibold">{tempName || "Maya"}</span>. I work with {firstName}.&rdquo;
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// /instance-ready/:reservationId
// ---------------------------------------------------------------------
export function InstanceReadyPage() {
  const navigate = useNavigate();
  const { data } = useSignupStore();
  const personaName = usePersonaStore((s) => s.name);
  const setMode = useInstanceStateStore((s) => s.setMode);

  useEffect(() => {
    try { confetti({ particleCount: 140, spread: 80, startVelocity: 42, origin: { y: 0.45 }, colors: ["#10B981", "#4F46E5", "#8B5CF6", "#F59E0B"], scalar: 1.0 }); } catch (_) { /* best */ }
    // Provisioning finished — flip instance to fresh mode
    setMode("fresh");
  }, [setMode]);

  const subdomain = `${(data.founderName || "you").split(" ")[0].toLowerCase()}-${(data.company || "co").toLowerCase().replace(/\s+/g, "")}.sellix.app`;
  const firstName = (data.founderName || "there").split(" ")[0];

  const goToWelcome = () => navigate("/welcome");

  return (
    <div className="min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-gradient-to-br from-emerald-50/30 via-white to-indigo-50/20">
      <FunnelNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14 text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 animate-in zoom-in-50 duration-300"><CheckCircle2 size={30} className="text-emerald-600" /></div>
        <h1 className="mt-4 text-[26px] sm:text-[34px] md:text-[40px] font-semibold tracking-tight text-slate-900 leading-tight">Your Sellyx is ready</h1>
        <p className="mt-2 text-[14px] text-slate-600 break-words">Your private instance is live at <span className="font-mono text-indigo-700 break-all">{subdomain}</span></p>
        <div className="mt-2 text-[11px] text-slate-500">Region: us-west-2 · OpenClaw v2.4.1 · GPT-5.5 · Encrypted at rest</div>

        <div className="mt-8 space-y-3 text-left">
          {/* Email card */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 min-w-0" data-testid="ready-email-card">
            <div className="flex flex-wrap items-center gap-2">
              <span className="h-7 w-7 rounded-md bg-rose-100 text-rose-700 flex items-center justify-center shrink-0"><Mail size={13} /></span>
              <div className="flex-1 min-w-0 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">Email sent</div>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold"><CheckCircle2 size={10} /> Delivered · just now</span>
            </div>
            <div className="mt-2 rounded-lg ring-1 ring-slate-200 bg-slate-50/40 p-3 min-w-0">
              <div className="text-[10px] text-slate-500 break-all">Sellyx Team &lt;onboarding@sellix.ai&gt; → {data.email || "you@…"}</div>
              <div className="mt-0.5 text-[13px] font-semibold text-slate-900">Your Sellyx is ready · Set up {personaName} in 5 minutes</div>
              <div className="mt-1 text-[11.5px] text-slate-600 leading-snug break-words">Welcome aboard, {firstName}. Your instance <span className="font-mono break-all">{subdomain}</span> is live. Tap the button below to finish setting up {personaName} — it takes about 5 minutes.</div>
            </div>
            <Button variant="ghost" size="sm" className="mt-2 h-9 sm:h-7 text-[11px]" onClick={() => toast("Opening Gmail…")} data-testid="ready-email-open">Open email →</Button>
          </div>

          {/* WhatsApp card */}
          <div className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4 min-w-0" data-testid="ready-whatsapp-card">
            <div className="flex flex-wrap items-center gap-2">
              <span className="h-7 w-7 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><MessageCircle size={13} /></span>
              <div className="flex-1 min-w-0 text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">WhatsApp notification sent</div>
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold"><CheckCircle2 size={10} /> Delivered · just now</span>
            </div>
            <div className="mt-2 ml-auto max-w-full sm:max-w-[80%] rounded-2xl rounded-br-md bg-emerald-100 text-slate-900 px-3 py-2 text-[12.5px] leading-snug break-words">
              Sellyx here · Your instance <span className="font-mono break-all">{subdomain}</span> is live. Setup link is in the email we just sent. — {personaName}
              <div className="text-right text-[9px] text-emerald-700 mt-1 flex items-center justify-end gap-0.5">just now · <span className="text-sky-600">✓✓</span></div>
            </div>
          </div>

          {/* Magic link CTA card */}
          <div className="rounded-2xl border border-indigo-300 ring-2 ring-indigo-100 bg-gradient-to-br from-white to-indigo-50/40 p-4 sm:p-5 text-center" data-testid="ready-magiclink-card">
            <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold">Magic link ready</div>
            <div className="mt-1 text-[16px] font-semibold text-slate-900">Set up {personaName} now</div>
            <div className="mt-1 text-[11.5px] text-slate-600">Takes about 5 minutes · skip steps any time</div>
            <Button size="lg" className="mt-3 h-11 text-[13px] px-5 w-full sm:w-auto" onClick={goToWelcome} data-testid="ready-cta">Set up {personaName} now <ArrowRight size={13} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// /welcome — pre-onboarding
// ---------------------------------------------------------------------
export function WelcomePage() {
  const navigate = useNavigate();
  const { data } = useSignupStore();
  const personaName = usePersonaStore((s) => s.name);
  const firstName = (data.founderName || "there").split(" ")[0];
  const subdomain = `${(data.founderName || "you").split(" ")[0].toLowerCase()}-${(data.company || "co").toLowerCase().replace(/\s+/g, "")}.sellix.app`;

  return (
    <div className="min-h-dvh w-full max-w-[100vw] overflow-x-hidden bg-gradient-to-br from-indigo-50/40 via-white to-violet-50/30 flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-[600px] w-full min-w-0 text-center">
        <div className="inline-flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md ring-8 ring-indigo-50"><Sparkles size={32} strokeWidth={2.25} /></div>
        <h1 className="mt-5 text-[24px] sm:text-[28px] md:text-[36px] font-semibold tracking-tight text-slate-900 leading-tight break-words">Welcome to your Sellyx instance, {firstName}.</h1>
        <p className="mt-3 text-[14px] text-slate-600 max-w-md mx-auto break-words">
          <span className="font-mono text-indigo-700 break-all">{subdomain}</span> is live and dedicated to you. Let&apos;s set up {personaName} — it&apos;ll take 5 minutes.
        </p>
        <div className="mt-6 flex items-center justify-center gap-1.5 flex-wrap">
          {[
            "Single-tenant — only you can access this",
            "5-minute setup",
            "Skip any step, finish later",
          ].map((p) => (
            <span key={p} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700"><CheckCircle2 size={10} className="shrink-0" /> {p}</span>
          ))}
        </div>
        <Button size="lg" className="mt-7 h-11 text-[13px] px-5 w-full sm:w-auto" onClick={() => navigate("/onboarding/welcome")} data-testid="welcome-cta">Let&apos;s set up {personaName} <ArrowRight size={13} /></Button>
        <div className="mt-4 text-[11px] text-slate-400 px-2">
          <button type="button" onClick={() => navigate("/today")} className="hover:text-slate-600 min-h-10 inline-flex items-center justify-center">I&apos;ll set up later — show me {personaName}&apos;s dashboard →</button>
        </div>
      </div>
    </div>
  );
}
